/**
 * Google Analytics 4 Utility Functions
 *
 * Provides centralized analytics tracking for conversion optimization
 * Events tracked: CTA clicks, demo views, persona switches, scroll depth, app installs
 */

import ReactGA from 'react-ga4';

// Initialize GA4 with measurement ID from environment
export const initGA = () => {
  const measurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

  if (!measurementId) {
    console.warn('GA4 Measurement ID not found in environment variables');
    return;
  }

  try {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        debug_mode: process.env.NODE_ENV === 'development',
      },
    });
    console.log('GA4 initialized successfully');
  } catch (error) {
    console.error('Failed to initialize GA4:', error);
  }
};

// Track page views
export const trackPageView = (path) => {
  ReactGA.send({ hitType: 'pageview', page: path });
};

// Event tracking functions for conversion optimization

/**
 * Track CTA button clicks
 * @param {string} ctaLabel - Label of the CTA (e.g., "Start Free Trial", "Get Started")
 * @param {string} section - Section where CTA is located (e.g., "hero", "pricing", "footer")
 */
export const trackCTAClick = (ctaLabel, section = 'unknown') => {
  ReactGA.event({
    category: 'CTA',
    action: 'cta_click',
    label: `${section}_${ctaLabel}`,
  });
};

/**
 * Track demo video views
 * @param {string} videoType - Type of video (e.g., "hero_autoplay", "modal_demo", "30sec_demo")
 */
export const trackVideoView = (videoType = 'demo') => {
  ReactGA.event({
    category: 'Engagement',
    action: 'view_demo',
    label: videoType,
  });
};

/**
 * Track hero video play
 */
export const trackHeroVideoPlay = () => {
  ReactGA.event({
    category: 'Engagement',
    action: 'video_play_hero',
  });
};

/**
 * Track app store badge clicks
 * @param {string} platform - "ios" or "android"
 * @param {string} section - Section where badge is located
 */
export const trackAppStoreClick = (platform, section = 'hero') => {
  const action = platform === 'ios' ? 'install_click_ios' : 'install_click_android';

  ReactGA.event({
    category: 'Conversion',
    action,
    label: section,
  });
};

/**
 * Track trial signup initiation
 * @param {string} source - Where the trial was started from
 */
export const trackTrialStart = (source = 'unknown') => {
  ReactGA.event({
    category: 'Conversion',
    action: 'trial_start',
    label: source,
  });
};

/**
 * Track persona tab selection
 * @param {string} persona - Selected persona ("professionals", "students", "entrepreneurs")
 */
export const trackPersonaSwitch = (persona) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'persona_tab',
    label: persona,
  });
};

/**
 * Track scroll depth milestones
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'scroll_depth',
    label: `${percentage}%`,
    nonInteraction: true,
  });
};

/**
 * Track feature demo clicks (15s demos in 3 Outcomes section)
 * @param {string} feature - Feature being demoed (e.g., "voice_to_task", "meeting_summary", "unified_tools")
 */
export const trackFeatureDemo = (feature) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'feature_demo_click',
    label: feature,
  });
};

/**
 * Track pricing plan selection
 * @param {string} plan - Plan selected (e.g., "free", "pro", "enterprise")
 */
export const trackPlanSelect = (plan) => {
  ReactGA.event({
    category: 'Conversion',
    action: 'plan_select',
    label: plan,
  });
};

/**
 * Track external link clicks
 * @param {string} destination - Where the link goes (e.g., "docs", "tutorials", "contact")
 */
export const trackExternalLink = (destination) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'external_link_click',
    label: destination,
  });
};

/**
 * Track navigation menu interactions
 * @param {string} item - Menu item clicked
 */
export const trackNavigation = (item) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'nav_click',
    label: item,
  });
};

// Custom event tracking for flexible use cases
export const trackCustomEvent = (category, action, label, value) => {
  ReactGA.event({
    category,
    action,
    label,
    value,
  });
};

// =============================================================================
// AUTHENTICATION & SIGNUP TRACKING
// =============================================================================

/**
 * Track signup flow initiation
 * @param {string} method - Authentication method ("email", "google", "apple")
 */
export const trackSignupStart = (method = 'email') => {
  ReactGA.event({
    category: 'Authentication',
    action: 'signup_start',
    label: method,
  });
};

/**
 * Track successful signup completion
 * @param {string} method - Authentication method used
 * @param {string} userId - User ID (optional, for user-scoped tracking)
 */
export const trackSignupComplete = (method = 'email', userId = null) => {
  ReactGA.event({
    category: 'Authentication',
    action: 'signup_complete',
    label: method,
  });

  if (userId) {
    ReactGA.set({ userId });
  }
};

/**
 * Track login attempt
 * @param {string} method - Authentication method ("email", "google", "apple")
 */
export const trackLoginAttempt = (method = 'email') => {
  ReactGA.event({
    category: 'Authentication',
    action: 'login_attempt',
    label: method,
  });
};

/**
 * Track successful login
 * @param {string} method - Authentication method used
 * @param {string} userId - User ID for session tracking
 */
export const trackLoginSuccess = (method = 'email', userId = null) => {
  ReactGA.event({
    category: 'Authentication',
    action: 'login_success',
    label: method,
  });

  if (userId) {
    ReactGA.set({ userId });
  }
};

/**
 * Track authentication errors
 * @param {string} errorType - Type of error (e.g., "wrong_password", "network_error")
 * @param {string} context - Where error occurred ("signup" or "login")
 */
export const trackAuthError = (errorType, context = 'login') => {
  ReactGA.event({
    category: 'Authentication',
    action: `${context}_error`,
    label: errorType,
  });
};

/**
 * Track password reset flow
 * @param {string} action - "request" or "complete"
 */
export const trackPasswordReset = (action = 'request') => {
  ReactGA.event({
    category: 'Authentication',
    action: 'password_reset',
    label: action,
  });
};

/**
 * Track login page view
 */
export const trackLoginPageView = () => {
  ReactGA.event({
    category: 'Navigation',
    action: 'login_page_view',
  });
};

// =============================================================================
// ONBOARDING TRACKING
// =============================================================================

/**
 * Track onboarding flow start
 */
export const trackOnboardingStart = () => {
  ReactGA.event({
    category: 'Onboarding',
    action: 'onboarding_start',
  });
};

/**
 * Track onboarding step interaction
 * @param {number} stepNumber - Step number (1-5)
 * @param {string} stepName - Name of the step
 * @param {string} action - "view", "complete", or "skip"
 */
export const trackOnboardingStep = (stepNumber, stepName, action = 'view') => {
  ReactGA.event({
    category: 'Onboarding',
    action: `onboarding_step_${action}`,
    label: `${stepNumber}_${stepName}`,
    value: stepNumber,
  });
};

/**
 * Track onboarding completion
 * @param {number} totalDuration - Total time spent in onboarding (seconds)
 * @param {number} stepsCompleted - Number of steps completed
 */
export const trackOnboardingComplete = (totalDuration = 0, stepsCompleted = 5) => {
  ReactGA.event({
    category: 'Onboarding',
    action: 'onboarding_complete',
    value: totalDuration,
  });
};

/**
 * Track onboarding abandonment
 * @param {number} stepNumber - Step where user abandoned
 * @param {string} stepName - Name of the step
 */
export const trackOnboardingAbandon = (stepNumber, stepName) => {
  ReactGA.event({
    category: 'Onboarding',
    action: 'onboarding_abandon',
    label: `${stepNumber}_${stepName}`,
    value: stepNumber,
  });
};

/**
 * Track integration connection during onboarding
 * @param {string} integrationType - Type of integration (e.g., "google_calendar", "outlook")
 * @param {boolean} success - Whether connection was successful
 */
export const trackIntegrationConnect = (integrationType, success = true) => {
  ReactGA.event({
    category: 'Onboarding',
    action: success ? 'integration_success' : 'integration_error',
    label: integrationType,
  });
};

/**
 * Track Pro upgrade during onboarding
 * @param {string} source - Where upgrade was triggered
 * @param {string} plan - Plan selected ("monthly" or "yearly")
 */
export const trackProUpgrade = (source, plan = 'monthly') => {
  ReactGA.event({
    category: 'Monetization',
    action: 'pro_upgrade_onboarding',
    label: `${source}_${plan}`,
  });
};

// =============================================================================
// TASK MANAGEMENT TRACKING
// =============================================================================

/**
 * Track task action (create, edit, complete, delete)
 * @param {string} action - Action performed ("create", "edit", "complete", "delete")
 * @param {string} taskType - "task" or "appointment"
 * @param {boolean} isFirst - Whether this is user's first task/appointment
 */
export const trackTaskAction = (action, taskType = 'task', isFirst = false) => {
  ReactGA.event({
    category: 'Tasks',
    action: `task_${action}`,
    label: isFirst ? `first_${taskType}` : taskType,
  });
};

/**
 * Track view type changes in agenda
 * @param {string} viewType - "day", "week", "month", "year", "unplanned"
 * @param {string} previousView - Previous view type
 */
export const trackViewChange = (viewType, previousView = null) => {
  ReactGA.event({
    category: 'Tasks',
    action: 'view_change',
    label: previousView ? `${previousView}_to_${viewType}` : viewType,
  });
};

/**
 * Track date navigation
 * @param {string} action - "previous", "next", or "today"
 */
export const trackDateNavigation = (action) => {
  ReactGA.event({
    category: 'Tasks',
    action: 'date_navigation',
    label: action,
  });
};

/**
 * Track filter usage
 * @param {string} filterType - "priority" or "project"
 * @param {string} filterValue - Selected filter value
 */
export const trackFilterUse = (filterType, filterValue) => {
  ReactGA.event({
    category: 'Tasks',
    action: 'filter_apply',
    label: `${filterType}_${filterValue}`,
  });
};

/**
 * Track search query
 * @param {number} queryLength - Length of search query
 */
export const trackSearchQuery = (queryLength) => {
  ReactGA.event({
    category: 'Tasks',
    action: 'search_query',
    value: queryLength,
  });
};

/**
 * Track modal interactions
 * @param {string} modalType - Type of modal ("add", "edit", "completed")
 * @param {string} action - "open", "save", or "abandon"
 * @param {object} metadata - Additional data (time_spent, fields_modified, etc.)
 */
export const trackModalInteraction = (modalType, action, metadata = {}) => {
  ReactGA.event({
    category: 'Tasks',
    action: `modal_${action}`,
    label: modalType,
    ...metadata,
  });
};

// =============================================================================
// NOTES TRACKING
// =============================================================================

/**
 * Track note action
 * @param {string} action - "create", "edit", "delete"
 * @param {boolean} isFirst - Whether this is user's first note
 * @param {object} metadata - Additional data (has_attachment, is_secure, etc.)
 */
export const trackNoteAction = (action, isFirst = false, metadata = {}) => {
  ReactGA.event({
    category: 'Notes',
    action: `note_${action}`,
    label: isFirst ? 'first_note' : 'note',
    ...metadata,
  });
};

/**
 * Track secure note toggle
 * @param {boolean} enabled - Whether secure mode was enabled
 */
export const trackSecureNoteToggle = (enabled) => {
  ReactGA.event({
    category: 'Notes',
    action: 'secure_note_toggle',
    label: enabled ? 'enabled' : 'disabled',
  });
};

// =============================================================================
// AI CHATBOT TRACKING
// =============================================================================

/**
 * Track chat action
 * @param {string} action - "session_start", "message_sent", "suggestion_click", "task_from_chat", "note_from_chat"
 * @param {object} metadata - Additional data (message_length, is_first_chat, etc.)
 */
export const trackChatAction = (action, metadata = {}) => {
  ReactGA.event({
    category: 'AI Chat',
    action: `chat_${action}`,
    ...metadata,
  });
};

// =============================================================================
// FEATURE ACCESS TRACKING
// =============================================================================

/**
 * Track feature access
 * @param {string} featureName - "calendar", "notes", "chatbot", "settings", "notifications", "projects"
 * @param {boolean} isFirstAccess - Whether this is the first time accessing this feature
 */
export const trackFeatureAccess = (featureName, isFirstAccess = false) => {
  ReactGA.event({
    category: 'Feature Access',
    action: 'feature_access',
    label: isFirstAccess ? `first_${featureName}` : featureName,
  });
};

/**
 * Track first use of any feature (activation event)
 * @param {string} featureName - Feature being used for the first time
 */
export const trackFeatureFirstUse = (featureName) => {
  ReactGA.event({
    category: 'Activation',
    action: 'feature_first_use',
    label: featureName,
  });
};

/**
 * Track dashboard view
 * @param {boolean} isFirstVisit - Whether this is user's first dashboard visit
 */
export const trackDashboardView = (isFirstVisit = false) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'dashboard_view',
    label: isFirstVisit ? 'first_visit' : 'return_visit',
  });
};

// =============================================================================
// CALENDAR TRACKING
// =============================================================================

/**
 * Track calendar action
 * @param {string} action - "view_change", "event_create", "event_drag", "event_click"
 * @param {object} metadata - Additional data (view_type, etc.)
 */
export const trackCalendarAction = (action, metadata = {}) => {
  ReactGA.event({
    category: 'Calendar',
    action: `calendar_${action}`,
    ...metadata,
  });
};

// =============================================================================
// SETTINGS TRACKING
// =============================================================================

/**
 * Track settings change
 * @param {string} settingType - "profile", "theme", "notifications", "calendar_integration"
 * @param {string} newValue - New value set
 */
export const trackSettingsChange = (settingType, newValue) => {
  ReactGA.event({
    category: 'Settings',
    action: 'settings_change',
    label: `${settingType}_${newValue}`,
  });
};

/**
 * Track settings tab view
 * @param {string} tabName - Tab being viewed
 */
export const trackSettingsTabView = (tabName) => {
  ReactGA.event({
    category: 'Settings',
    action: 'settings_tab_view',
    label: tabName,
  });
};

// =============================================================================
// NOTIFICATIONS TRACKING
// =============================================================================

/**
 * Track notification action
 * @param {string} action - "view", "click", "dismiss", "mark_read"
 * @param {string} notificationType - Type of notification
 */
export const trackNotificationAction = (action, notificationType = 'general') => {
  ReactGA.event({
    category: 'Notifications',
    action: `notification_${action}`,
    label: notificationType,
  });
};

// =============================================================================
// PROJECT TRACKING
// =============================================================================

/**
 * Track project action
 * @param {string} action - "create", "edit", "switch", "archive"
 * @param {string} projectId - Project identifier (optional)
 */
export const trackProjectAction = (action, projectId = null) => {
  ReactGA.event({
    category: 'Projects',
    action: `project_${action}`,
    label: projectId || 'project',
  });
};

// =============================================================================
// SESSION & ENGAGEMENT TRACKING
// =============================================================================

/**
 * Track session metrics
 * @param {string} action - "session_start" or "session_end"
 * @param {number} duration - Session duration in seconds (for session_end)
 * @param {number} actionsCount - Number of actions performed in session
 */
export const trackSessionMetrics = (action, duration = 0, actionsCount = 0) => {
  ReactGA.event({
    category: 'Engagement',
    action,
    value: action === 'session_end' ? duration : actionsCount,
  });
};

/**
 * Track sidebar navigation
 * @param {string} featureName - Feature clicked in sidebar
 */
export const trackSidebarNavigation = (featureName) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'sidebar_navigation',
    label: featureName,
  });
};

// =============================================================================
// MONETIZATION TRACKING
// =============================================================================

/**
 * Track Pro upgrade flow
 * @param {string} action - "limit_hit", "prompt_view", "click", "checkout_start", "complete", "error"
 * @param {string} source - Where the upgrade action originated
 * @param {object} metadata - Additional data (plan, price, error_type, etc.)
 */
export const trackProUpgradeFlow = (action, source, metadata = {}) => {
  ReactGA.event({
    category: 'Monetization',
    action: `pro_${action}`,
    label: source,
    ...metadata,
  });
};

/**
 * Track feature limit encounters
 * @param {string} featureName - Feature that hit limit
 */
export const trackFeatureLimitHit = (featureName) => {
  ReactGA.event({
    category: 'Monetization',
    action: 'feature_limit_hit',
    label: featureName,
  });
};

// =============================================================================
// RETENTION & CROSS-PLATFORM TRACKING
// =============================================================================

/**
 * Track retention signal
 * @param {string} signalType - "return_visit", "streak_milestone", "feature_reengagement", etc.
 * @param {object} metadata - Additional data (days_since_signup, streak_days, etc.)
 */
export const trackRetentionSignal = (signalType, metadata = {}) => {
  ReactGA.event({
    category: 'Retention',
    action: signalType,
    ...metadata,
  });
};

/**
 * Track cross-platform action
 * @param {string} action - "notification_permission", "extension_prompt", "extension_install"
 * @param {string} platform - "web", "extension", "mobile"
 */
export const trackCrossPlatformAction = (action, platform = 'web') => {
  ReactGA.event({
    category: 'Cross-Platform',
    action,
    label: platform,
  });
};

// =============================================================================
// LANDING PAGE SPECIFIC TRACKING
// =============================================================================

/**
 * Track footer link clicks
 * @param {string} linkName - Name of the footer link
 * @param {string} section - Footer section ("product", "company", "resources", "legal")
 */
export const trackFooterLink = (linkName, section) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'footer_link_click',
    label: `${section}_${linkName}`,
  });
};

/**
 * Track social media clicks
 * @param {string} platform - "twitter", "linkedin", "facebook", "instagram"
 */
export const trackSocialClick = (platform) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'social_media_click',
    label: platform,
  });
};

/**
 * Track testimonial carousel interaction
 * @param {string} action - "scroll", "click", "pause"
 */
export const trackTestimonialInteraction = (action) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'testimonial_interaction',
    label: action,
  });
};

/**
 * Track use case card clicks
 * @param {string} useCaseName - Name of the use case
 */
export const trackUseCaseClick = (useCaseName) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'use_case_click',
    label: useCaseName,
  });
};

/**
 * Track integration icon clicks
 * @param {string} integrationName - Name of the integration
 */
export const trackIntegrationClick = (integrationName) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'integration_click',
    label: integrationName,
  });
};

/**
 * Track feature detail modal views
 * @param {string} featureName - Name of the feature being viewed
 */
export const trackFeatureDetail = (featureName) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'feature_detail_view',
    label: featureName,
  });
};

// =============================================================================
// USER PROPERTIES HELPER
// =============================================================================

/**
 * Set user properties for enhanced tracking
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  ReactGA.set(properties);
};

export default {
  initGA,
  trackPageView,
  trackCTAClick,
  trackVideoView,
  trackHeroVideoPlay,
  trackAppStoreClick,
  trackTrialStart,
  trackPersonaSwitch,
  trackScrollDepth,
  trackFeatureDemo,
  trackPlanSelect,
  trackExternalLink,
  trackNavigation,
  trackCustomEvent,
  // Authentication
  trackSignupStart,
  trackSignupComplete,
  trackLoginAttempt,
  trackLoginSuccess,
  trackAuthError,
  trackPasswordReset,
  trackLoginPageView,
  // Onboarding
  trackOnboardingStart,
  trackOnboardingStep,
  trackOnboardingComplete,
  trackOnboardingAbandon,
  trackIntegrationConnect,
  trackProUpgrade,
  // Tasks
  trackTaskAction,
  trackViewChange,
  trackDateNavigation,
  trackFilterUse,
  trackSearchQuery,
  trackModalInteraction,
  // Notes
  trackNoteAction,
  trackSecureNoteToggle,
  // Chat
  trackChatAction,
  // Features
  trackFeatureAccess,
  trackFeatureFirstUse,
  trackDashboardView,
  trackCalendarAction,
  // Settings
  trackSettingsChange,
  trackSettingsTabView,
  // Notifications
  trackNotificationAction,
  // Projects
  trackProjectAction,
  // Engagement
  trackSessionMetrics,
  trackSidebarNavigation,
  // Monetization
  trackProUpgradeFlow,
  trackFeatureLimitHit,
  // Retention
  trackRetentionSignal,
  trackCrossPlatformAction,
  // Landing Page
  trackFooterLink,
  trackSocialClick,
  trackTestimonialInteraction,
  trackUseCaseClick,
  trackIntegrationClick,
  trackFeatureDetail,
  // User Properties
  setUserProperties,
};
